import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../../services/api';

// ── Overlay / modal shell ──────────────────────────────────────────────────

const OVERLAY = {
  position:        'fixed',
  inset:           0,
  background:      'rgba(0,0,0,0.55)',
  display:         'flex',
  alignItems:      'center',
  justifyContent:  'center',
  zIndex:          1000,
  padding:         '1rem',
};

const MODAL = {
  background:    '#fff',
  borderRadius:  '12px',
  width:         '100%',
  maxWidth:      '540px',
  maxHeight:     '90vh',
  overflowY:     'auto',
  boxShadow:     '0 24px 80px rgba(0,0,0,0.35)',
  display:       'flex',
  flexDirection: 'column',
};

const MODAL_HEAD = {
  padding:         '1.5rem 1.75rem 1rem',
  borderBottom:    '1px solid #e5e7eb',
  display:         'flex',
  justifyContent:  'space-between',
  alignItems:      'center',
  flexShrink:      0,
};

const MODAL_BODY = {
  padding:  '1.5rem 1.75rem',
  flexGrow: 1,
};

const MODAL_FOOT = {
  padding:       '1rem 1.75rem 1.5rem',
  borderTop:     '1px solid #e5e7eb',
  display:       'flex',
  gap:           '0.75rem',
  justifyContent:'flex-end',
  flexShrink:    0,
};

// ── Step indicator ─────────────────────────────────────────────────────────

function StepDots({ step, total }) {
  return (
    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '1.25rem' }}>
      {Array.from({ length: total }, (_, i) => (
        <div
          key={i}
          style={{
            width:        i === step ? 20 : 8,
            height:       8,
            borderRadius: 4,
            background:   i === step ? '#0f1e3d' : '#d1d5db',
            transition:   'all 0.2s',
          }}
        />
      ))}
      <span style={{ fontSize: '0.78rem', color: '#6b7280', marginLeft: '0.25rem' }}>
        Step {step + 1} of {total}
      </span>
    </div>
  );
}

// ── Step 1: Contestant search / create ────────────────────────────────────

function Step1Contestant({ value, onChange }) {
  const [query,   setQuery]   = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [mode, setMode] = useState('search'); // 'search' | 'new'
  const debounceRef = useRef(null);

  // Populate search box if an existing contestant was already selected
  useEffect(() => {
    if (value.contestant_id && value.full_name) {
      setQuery(value.full_name);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const doSearch = useCallback((q) => {
    if (!q.trim()) { setResults([]); return; }
    setSearching(true);
    api.get(`/api/admin/contestants/search?q=${encodeURIComponent(q)}`)
      .then((res) => setResults(res.data.contestants || []))
      .catch(() => setResults([]))
      .finally(() => setSearching(false));
  }, []);

  function handleQueryChange(e) {
    const q = e.target.value;
    setQuery(q);
    // Clear selection if user edits the query
    if (value.contestant_id) {
      onChange({ contestant_id: null, full_name: '', email: '', is_member: 'non-member' });
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(q), 300);
  }

  function selectContestant(c) {
    onChange({
      contestant_id: c.contestant_id,
      full_name:     c.full_name,
      email:         c.email,
      is_member:     c.is_member ? 'member' : 'non-member',
    });
    setQuery(c.full_name);
    setResults([]);
  }

  function handleNewField(field, val) {
    onChange({ ...value, contestant_id: null, [field]: val });
  }

  return (
    <div>
      <StepDots step={0} total={3} />
      <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', color: '#0f1e3d' }}>
        Contestant
      </h3>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        <button
          type="button"
          className={`c-btn c-btn--sm${mode === 'search' ? '' : ' c-btn--outline'}`}
          onClick={() => setMode('search')}
        >
          Search existing
        </button>
        <button
          type="button"
          className={`c-btn c-btn--sm${mode === 'new' ? '' : ' c-btn--outline'}`}
          onClick={() => {
            setMode('new');
            onChange({ contestant_id: null, full_name: '', email: '', is_member: 'non-member' });
            setQuery('');
            setResults([]);
          }}
        >
          New contestant
        </button>
      </div>

      {mode === 'search' ? (
        <div className="c-form-group" style={{ position: 'relative' }}>
          <label className="c-label">Search by name or email</label>
          <input
            className="c-input"
            value={query}
            onChange={handleQueryChange}
            placeholder="Start typing…"
            autoFocus
          />
          {searching && (
            <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.35rem' }}>
              Searching…
            </div>
          )}
          {results.length > 0 && !value.contestant_id && (
            <div style={{
              position:   'absolute',
              top:        '100%',
              left:       0,
              right:      0,
              background: '#fff',
              border:     '1px solid #e5e7eb',
              borderRadius: '6px',
              boxShadow:  '0 4px 16px rgba(0,0,0,0.12)',
              zIndex:     10,
              maxHeight:  200,
              overflowY:  'auto',
            }}>
              {results.map((c) => (
                <button
                  key={c.contestant_id}
                  type="button"
                  onClick={() => selectContestant(c)}
                  style={{
                    display:    'block',
                    width:      '100%',
                    textAlign:  'left',
                    padding:    '0.65rem 1rem',
                    background: 'none',
                    border:     'none',
                    cursor:     'pointer',
                    fontSize:   '0.875rem',
                    borderBottom: '1px solid #f3f4f6',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                >
                  <span style={{ fontWeight: 600 }}>{c.full_name}</span>
                  <span style={{ color: '#6b7280', marginLeft: '0.5rem' }}>{c.email}</span>
                  {c.is_member && (
                    <span style={{
                      marginLeft:   '0.5rem',
                      fontSize:     '0.72rem',
                      background:   '#d4af37',
                      color:        '#1a1a2e',
                      borderRadius: 3,
                      padding:      '1px 5px',
                      fontWeight:   700,
                    }}>Member</span>
                  )}
                </button>
              ))}
            </div>
          )}
          {value.contestant_id && (
            <div style={{
              marginTop:  '0.5rem',
              padding:    '0.6rem 0.9rem',
              background: '#f0fdf4',
              border:     '1px solid #bbf7d0',
              borderRadius: '6px',
              fontSize:   '0.875rem',
              color:      '#14532d',
            }}>
              Selected: <strong>{value.full_name}</strong> ({value.email})
              {' '}
              <button
                type="button"
                onClick={() => {
                  onChange({ contestant_id: null, full_name: '', email: '', is_member: 'non-member' });
                  setQuery('');
                }}
                style={{
                  background: 'none', border: 'none', color: '#dc2626',
                  cursor: 'pointer', fontSize: '0.8rem', marginLeft: '0.35rem',
                }}
              >
                ✕ Clear
              </button>
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="c-form-group">
            <label className="c-label">Full name *</label>
            <input
              className="c-input"
              value={value.full_name}
              onChange={(e) => handleNewField('full_name', e.target.value)}
              autoFocus
            />
          </div>
          <div className="c-form-group">
            <label className="c-label">Email address *</label>
            <input
              className="c-input"
              type="email"
              value={value.email}
              onChange={(e) => handleNewField('email', e.target.value)}
            />
          </div>
          <div className="c-form-group">
            <span className="c-label">SCALA membership</span>
            <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.35rem' }}>
              {['member', 'non-member'].map((opt) => (
                <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.9rem' }}>
                  <input
                    type="radio"
                    value={opt}
                    checked={value.is_member === opt}
                    onChange={() => handleNewField('is_member', opt)}
                    style={{ accentColor: '#0f1e3d' }}
                  />
                  {opt.charAt(0).toUpperCase() + opt.slice(1)}
                </label>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Step 2: Song details ───────────────────────────────────────────────────

function Step2Song({ value, onChange, categories }) {
  function set(field, val) {
    onChange({ ...value, [field]: val });
  }

  return (
    <div>
      <StepDots step={1} total={3} />
      <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', color: '#0f1e3d' }}>
        Song Details
      </h3>

      <div className="c-form-group">
        <label className="c-label">Song title *</label>
        <input
          className="c-input"
          value={value.title}
          onChange={(e) => set('title', e.target.value)}
          autoFocus
        />
      </div>

      <div className="c-form-group">
        <label className="c-label">Category *</label>
        <select
          className="c-input"
          value={value.category_id}
          onChange={(e) => set('category_id', e.target.value)}
        >
          <option value="">— Select category —</option>
          {categories.map((cat) => (
            <option key={cat.id} value={cat.id}>{cat.name}</option>
          ))}
        </select>
      </div>

      <div className="c-form-group">
        <label className="c-label">
          Artist name <span className="c-label-optional">(optional)</span>
        </label>
        <input
          className="c-input"
          value={value.artist}
          onChange={(e) => set('artist', e.target.value)}
        />
      </div>

      <div className="c-form-group">
        <label className="c-label">
          Audio file <span className="c-label-optional">(optional — MP3, WAV, M4A, etc.)</span>
        </label>
        <input
          type="file"
          accept="audio/*,.mp3,.wav,.m4a,.aac,.ogg,.flac"
          className="c-input"
          style={{ padding: '0.4rem 0.6rem' }}
          onChange={(e) => set('audioFile', e.target.files[0] || null)}
        />
        {value.audioFile && (
          <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.25rem' }}>
            {value.audioFile.name}
          </div>
        )}
      </div>

      <div className="c-form-group">
        <label className="c-label">
          Lyrics <span className="c-label-optional">(optional)</span>
        </label>
        <textarea
          className="c-input"
          rows={5}
          value={value.lyrics_text}
          onChange={(e) => set('lyrics_text', e.target.value)}
          style={{ resize: 'vertical', fontFamily: 'inherit' }}
        />
      </div>
    </div>
  );
}

// ── Step 3: Confirm ────────────────────────────────────────────────────────

function Step3Confirm({ contestant, song, categories }) {
  const catName = categories.find((c) => String(c.id) === String(song.category_id))?.name || '—';

  return (
    <div>
      <StepDots step={2} total={3} />
      <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', color: '#0f1e3d' }}>
        Confirm &amp; Submit
      </h3>

      <div style={{
        background: '#f9fafb',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '1rem',
        marginBottom: '1rem',
        fontSize: '0.875rem',
      }}>
        <div style={{ fontWeight: 700, marginBottom: '0.5rem', color: '#374151' }}>Contestant</div>
        <div>{contestant.full_name} — {contestant.email}</div>
        <div style={{ color: '#6b7280', marginTop: '0.15rem' }}>
          {contestant.contestant_id ? 'Existing account' : 'New account will be created'}
          {' · '}
          {contestant.is_member === 'member' ? 'Member' : 'Non-member'}
        </div>
      </div>

      <div style={{
        background: '#f9fafb',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '1rem',
        fontSize: '0.875rem',
      }}>
        <div style={{ fontWeight: 700, marginBottom: '0.5rem', color: '#374151' }}>Song</div>
        <div><strong>{song.title}</strong></div>
        <div style={{ color: '#6b7280', marginTop: '0.15rem' }}>
          Category: {catName}
          {song.artist && ` · Artist: ${song.artist}`}
        </div>
        {song.audioFile && (
          <div style={{ color: '#6b7280', marginTop: '0.15rem' }}>Audio: {song.audioFile.name}</div>
        )}
      </div>

      <div style={{
        marginTop: '1rem',
        padding: '0.75rem 1rem',
        background: '#f0fdf4',
        border: '1px solid #bbf7d0',
        borderRadius: '6px',
        fontSize: '0.875rem',
        color: '#14532d',
        fontWeight: 600,
      }}>
        Payment status will be set to <strong>Paid</strong> immediately.
      </div>
    </div>
  );
}

// ── Main modal ─────────────────────────────────────────────────────────────

export default function AdminSongEntryModal({ onClose, onSuccess }) {
  const [step, setStep] = useState(0);
  const [categories, setCategories] = useState([]);
  const [loadingCats, setLoadingCats] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const [contestant, setContestant] = useState({
    contestant_id: null,
    full_name:     '',
    email:         '',
    is_member:     'non-member',
  });

  const [song, setSong] = useState({
    title:       '',
    category_id: '',
    artist:      '',
    lyrics_text: '',
    audioFile:   null,
  });

  useEffect(() => {
    api.get('/api/admin/categories')
      .then((res) => {
        setCategories((res.data.categories || []).filter((c) => c.is_active));
      })
      .catch(() => {})
      .finally(() => setLoadingCats(false));
  }, []);

  // ── Validation per step ──────────────────────────────────────────────────

  function validateStep0() {
    if (contestant.contestant_id) return null;
    if (!contestant.full_name.trim()) return 'Full name is required.';
    if (!contestant.email.trim())     return 'Email address is required.';
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(contestant.email.trim())) return 'Enter a valid email address.';
    return null;
  }

  function validateStep1() {
    if (!song.title.trim())    return 'Song title is required.';
    if (!song.category_id)     return 'Category is required.';
    return null;
  }

  function handleNext() {
    setSubmitError('');
    if (step === 0) {
      const err = validateStep0();
      if (err) { setSubmitError(err); return; }
    }
    if (step === 1) {
      const err = validateStep1();
      if (err) { setSubmitError(err); return; }
    }
    setStep((s) => s + 1);
  }

  async function handleSubmit() {
    setSubmitting(true);
    setSubmitError('');
    try {
      const fd = new FormData();

      if (contestant.contestant_id) {
        fd.append('contestant_id', contestant.contestant_id);
      } else {
        fd.append('email',     contestant.email.trim().toLowerCase());
        fd.append('full_name', contestant.full_name.trim());
        fd.append('is_member', contestant.is_member === 'member' ? 'true' : 'false');
      }

      fd.append('title',       song.title.trim());
      fd.append('category_id', song.category_id);
      if (song.artist.trim())      fd.append('artist',      song.artist.trim());
      if (song.lyrics_text.trim()) fd.append('lyrics_text', song.lyrics_text.trim());
      if (song.audioFile)          fd.append('audio',       song.audioFile);

      await api.post('/api/admin/songs/admin-submit', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      onSuccess();
    } catch (err) {
      setSubmitError(err.response?.data?.error ?? 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div style={OVERLAY} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={MODAL}>

        <div style={MODAL_HEAD}>
          <div>
            <div style={{ fontWeight: 700, fontSize: '1.05rem', color: '#0f1e3d' }}>
              Admin Song Entry
            </div>
            <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.15rem' }}>
              Cash / in-person submission — payment marked as paid
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: '1.25rem', color: '#9ca3af', lineHeight: 1, padding: '0.25rem',
            }}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div style={MODAL_BODY}>
          {loadingCats ? (
            <div className="c-loading"><div className="c-spinner" /></div>
          ) : (
            <>
              {step === 0 && (
                <Step1Contestant value={contestant} onChange={setContestant} />
              )}
              {step === 1 && (
                <Step2Song value={song} onChange={setSong} categories={categories} />
              )}
              {step === 2 && (
                <Step3Confirm contestant={contestant} song={song} categories={categories} />
              )}

              {submitError && (
                <div className="c-alert" style={{ marginTop: '1rem' }}>
                  {submitError}
                </div>
              )}
            </>
          )}
        </div>

        <div style={MODAL_FOOT}>
          {step > 0 && (
            <button
              type="button"
              className="c-btn c-btn--outline"
              onClick={() => { setSubmitError(''); setStep((s) => s - 1); }}
              disabled={submitting}
            >
              Back
            </button>
          )}
          <button
            type="button"
            className="c-btn c-btn--outline"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          {step < 2 ? (
            <button
              type="button"
              className="c-btn"
              onClick={handleNext}
              disabled={loadingCats}
            >
              Next →
            </button>
          ) : (
            <button
              type="button"
              className="c-btn"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? (
                <><span className="c-spinner c-spinner--sm c-spinner--white" aria-hidden="true" /> Submitting…</>
              ) : (
                'Submit Song'
              )}
            </button>
          )}
        </div>

      </div>
    </div>
  );
}
